/* -------------- Common -------------- */

export const cultures = ["tr-TR", "en-US"] as const;

export type Culture = typeof cultures[number];

export const cultureLabels: Record<Culture, string> = {
	"tr-TR": "Türkçe",
	"en-US": "İngilizce",
};

export interface ApiResponse<T> {
	result: T;
	error: { code?: number; message?: string } | string | null;
	isSuccessful: boolean;
}

export interface ListResult<T> {
	items: T[];
}

export type OperationStatus =
	| { type: "idle" }
	| { type: "loading"; operation: "add" | "fetch" | "update" | "delete" };

/* -------------- LIST (response) -------------- */

export interface VolunteersHighlightListItem {
	highlightId: string;
	title: string;
	description?: string | null;
	isActive: boolean;
	displayOrder: number;
	creationDate?: string | null;
}

export type VolunteersHighlightListApiResponse = ApiResponse<ListResult<VolunteersHighlightListItem>>;

/* -------------- GET (response) -------------- */

export interface VolunteersHighlightLocalizable {
	highlightLocalizableId: string;
	culture: string;
	title: string;
	description?: string | null;
	imageUrl: string;
}

export interface VolunteersHighlightDetail {
	highlightId: string;
	isActive: boolean;
	displayOrder: number;
	volunteersHighlightLocalizable: VolunteersHighlightLocalizable[];
}

export type VolunteersHighlightGetApiResponse = ApiResponse<VolunteersHighlightDetail>;

/* -------------- ADD (form = request) -------------- */

export interface VolunteersHighlightLocalizableFormData {
	culture: Culture;
	title: string;
	description: string;
	image: File | null;
}

export interface VolunteersHighlightFormData {
	isActive: boolean;
	displayOrder?: number;
	volunteersHighlightLocalizable: VolunteersHighlightLocalizableFormData[];
}

export const getDefaultVolunteersHighlightFormData = (): VolunteersHighlightFormData => ({
	isActive: false,
	displayOrder: undefined,
	volunteersHighlightLocalizable: cultures.map(culture => ({
		culture,
		title: "",
		description: "",
		image: null,
	})),
});

/* -------------- EDIT (form state, complete) -------------- */

export interface VolunteersHighlightEditLocalizable extends VolunteersHighlightLocalizableFormData {
	highlightLocalizableId: string;
	imageUrl: string;
}

export interface VolunteersHighlightEditFormData {
	highlightId: string;
	isActive: boolean;
	displayOrder?: number;
	volunteersHighlightLocalizable: VolunteersHighlightEditLocalizable[];
}

// Every culture gets a row so the form always shows both sections; a culture missing on the
// server keeps an empty id and is sent as a new localizable only when the user fills it in.
export const toVolunteersHighlightEditFormData = (detail: VolunteersHighlightDetail): VolunteersHighlightEditFormData => ({
	highlightId: detail.highlightId,
	isActive: detail.isActive,
	displayOrder: detail.displayOrder,
	volunteersHighlightLocalizable: cultures.map(culture => {
		const localizable = detail.volunteersHighlightLocalizable.find(item => item.culture === culture);
		return {
			highlightLocalizableId: localizable?.highlightLocalizableId ?? "",
			culture,
			title: localizable?.title ?? "",
			description: localizable?.description ?? "",
			imageUrl: localizable?.imageUrl ?? "",
			image: null,
		};
	}),
});

/* -------------- UPDATE (PATCH request, partial - only changed fields) -------------- */

export interface UpdateVolunteersHighlightLocalizable {
	highlightLocalizableId: string;
	culture: string;
	title?: string;
	description?: string;
	image?: File;
}

export interface UpdateVolunteersHighlightRequest {
	highlightId: string;
	isActive?: boolean;
	displayOrder?: number | null;
	volunteersHighlightLocalizable?: UpdateVolunteersHighlightLocalizable[];
}

/* -------------- Rules (kept in sync with the backend validator) -------------- */

export const VolunteersHighlightRules = {
	titleMaxLength: 50,
	descriptionMaxLength: 125,
	textPattern: /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@]*$/u,
	imageMaxFileSize: 20 * 1024 * 1024,
	imageMimeTypes: ["image/jpeg", "image/jpg", "image/png"],
	imageDimension: { width: 332, height: 332 },
} as const;
